import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChevronLeft, ChevronRight, MapPin, Navigation2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { fmtLongDate, todayISO, KIND } from "@/lib/format";
import { StatusBadge, SectionLabel } from "@/components/Brand";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { useJobActions } from "@/pages/admin/useJobActions";
import { useTechTracking, TechTrackingLayer, TechTrackingPanel } from "@/components/TechTracking";

const PALETTE = ["#61a229", "#0ea5e9", "#8b5cf6", "#ef4444", "#14b8a6", "#f97316", "#0f1710", "#ec4899"];
const PENDING = "#f59e0b";
const LAYERS = [["tech", "Interventi tecnici"], ["sites", "Cantieri"], ["staff", "Posizione dipendenti"], ["pending", "Da assegnare"], ["done", "Completati"], ["legend", "Legenda"]];
const LAYER_DEFAULT = Object.fromEntries(LAYERS.map(([k]) => [k, true]));
const DONE = "#94a3b8";
const DEFAULT_CENTER = [42.5, 12.5];

const shiftDate = (d, n) => {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};

const latlng = (j) => [j.location.coordinates[1], j.location.coordinates[0]];

function distanceKm(a, b) {
  const [la1, lo1] = latlng(a), [la2, lo2] = latlng(b);
  const r = Math.PI / 180;
  const h = Math.sin(((la2 - la1) * r) / 2) ** 2 + Math.cos(la1 * r) * Math.cos(la2 * r) * Math.sin(((lo2 - lo1) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

const pin = (color, label, active, site) =>
  L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: site
      ? `<div style="width:30px;height:30px;border-radius:6px;transform:rotate(45deg);background:${color};display:grid;place-items:center;border:3px solid ${active ? "#0f1710" : "#fff"};box-shadow:0 2px 8px rgba(0,0,0,.35)"><span style="transform:rotate(-45deg);color:#fff;font:700 10px 'JetBrains Mono',monospace">C</span></div>`
      : `<div style="width:30px;height:30px;border-radius:50%;background:${color};color:#fff;display:grid;place-items:center;font:700 11px 'JetBrains Mono',monospace;border:3px solid ${active ? "#0f1710" : "#fff"};box-shadow:0 2px 8px rgba(0,0,0,.35)">${label}</div>`,
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

function JobList({ title, items, empty, selectedId, colorOf, onPick, testid }) {
  return (
    <div className="rounded-2xl border bg-white">
      <p className="border-b px-4 py-3 font-display font-semibold">{title} ({items.length})</p>
      <div className="max-h-[320px] divide-y overflow-y-auto" data-testid={testid}>
        {items.map((j, i) => (
          <button key={j.id} onClick={() => onPick(j)} data-testid={`${testid}-item-${i}`}
            className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${j.id === selectedId ? "bg-[#a2ca6b]/15" : ""}`}>
            <span className={`mt-1 h-3 w-3 shrink-0 ${j.kind === "cantiere" ? "rotate-45 rounded-sm" : "rounded-full"}`} style={{ background: colorOf(j) }} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{j.start} · {j.kind === "cantiere" ? j.site?.name || j.client.last_name : `${j.client.first_name} ${j.client.last_name}`}</span>
              <span className="flex items-center gap-1 truncate text-xs text-slate-500">
                {j.location ? <MapPin className="h-3 w-3" /> : <Navigation2 className="h-3 w-3 text-red-400" />}
                {j.client.city}{!j.location && " · senza posizione"} · {j.technician_name || "da assegnare"}{j.helpers?.length ? ` +${j.helpers.length}` : ""}
              </span>
            </span>
            <StatusBadge status={j.status} />
          </button>
        ))}
        {!items.length && <p className="px-4 py-6 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>{empty}</p>}
      </div>
    </div>
  );
}

function NearbyPanel({ job, nearby, onAssign }) {
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-4 rise" data-testid="map-selected-job">
      <SectionLabel>Richiesta selezionata</SectionLabel>
      <div>
        <p className="font-display text-lg font-semibold">{job.client.first_name} {job.client.last_name}</p>
        <p className="text-sm text-slate-500">{KIND[job.kind]} · {job.start}–{job.end} · {job.client.city}</p>
      </div>
      {nearby.length ? (
        <div className="space-y-1.5" data-testid="map-nearby-list">
          <p className="text-xs font-semibold text-slate-600">Tecnici già in zona oggi</p>
          {nearby.map((n, i) => (
            <button key={n.techId} onClick={() => onAssign({ ...job, technician_id: n.techId })} data-testid={`map-nearby-tech-${i}`}
              className="choice flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm">
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: n.color }} />{n.name}</span>
              <span className="font-mono text-xs text-slate-500">{n.km.toFixed(1)} km · {n.count} int.</span>
            </button>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500" data-testid="map-nearby-none">Nessun tecnico assegnato in zona in questa data.</p>}
      <Button onClick={() => onAssign(job)} className="w-full rounded-full" data-testid="map-assign-button">
        <UserCheck className="h-4 w-4" /> Approva e assegna
      </Button>
    </div>
  );
}

export default function AdminMap() {
  const [date, setDate] = useState(todayISO());
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/jobs", { params: { date, status: "in_attesa,confermato,in_corso,completato" } }).then((r) => setJobs(r.data));
  }, [date]);
  useEffect(() => { load(); setSelectedId(null); }, [load]);
  const actions = useJobActions(load);
  const tracking = useTechTracking(date);
  const [layers, setLayers] = useState(() => {
    try { return { ...LAYER_DEFAULT, ...JSON.parse(localStorage.getItem("mdn_map_layers") || "{}") }; } catch { return LAYER_DEFAULT; }
  });
  const toggle = (k) => setLayers((p) => { const n = { ...p, [k]: !p[k] }; localStorage.setItem("mdn_map_layers", JSON.stringify(n)); return n; });
  const [techFilter, setTechFilter] = useState("");
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/admin/technicians"), api.get("/admin/technicians", { params: { role: "responsabile" } })])
      .then(([a, b]) => setStaff([...a.data, ...b.data].filter((t) => t.active).sort((x, y) => x.name.localeCompare(y.name)))).catch(() => {});
  }, []);
  const { setTechId, techId: routeTech } = tracking;
  useEffect(() => { if (techFilter && routeTech !== techFilter) setTechId(techFilter); }, [techFilter, routeTech, date, setTechId]);
  useEffect(() => { if (!techFilter) setTechId(""); }, [techFilter, setTechId]);
  const ofTech = (j) => !techFilter || j.technician_id === techFilter || (j.helpers || []).some((h) => h.id === techFilter);
  const visible = (j) => ofTech(j) && (j.kind === "cantiere" ? layers.sites : layers.tech) && (layers.done || j.status !== "completato") && (layers.pending || j.status !== "in_attesa");
  const liveShown = techFilter ? tracking.live.filter((t) => t.technician_id === techFilter) : tracking.live;

  const techColors = useMemo(() => {
    const ids = [...new Set(jobs.filter((j) => j.technician_id).map((j) => j.technician_id))];
    return Object.fromEntries(ids.map((id, i) => [id, PALETTE[i % PALETTE.length]]));
  }, [jobs]);
  const located = jobs.filter((j) => j.location && visible(j));
  const points = useMemo(() => located.map(latlng), [jobs, layers]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorOf = (j) => (j.status === "in_attesa" ? PENDING : j.status === "completato" ? DONE : techColors[j.technician_id] || PALETTE[0]);
  const selected = jobs.find((j) => j.id === selectedId);
  const pending = jobs.filter((j) => j.status === "in_attesa");
  const sites = jobs.filter((j) => j.kind === "cantiere" && visible(j));
  const tech = jobs.filter((j) => j.kind !== "cantiere" && visible(j));
  const isToday = date === todayISO();
  const pick = (j) => (j.location ? setSelectedId(j.id) : actions.openDetail(j));
  const legend = Object.entries(techColors).map(([id, color]) => ({
    id, color, name: jobs.find((j) => j.technician_id === id)?.technician_name, count: jobs.filter((j) => j.technician_id === id).length,
  }));

  const nearby = useMemo(() => {
    if (!selected?.location) return [];
    const best = {};
    jobs.filter((j) => j.technician_id && j.location && j.status !== "in_attesa").forEach((j) => {
      const km = distanceKm(selected, j);
      const b = best[j.technician_id];
      if (!b || km < b.km) best[j.technician_id] = { techId: j.technician_id, name: j.technician_name, km, color: techColors[j.technician_id], count: (b?.count || 0) + 1 };
      else b.count += 1;
    });
    return Object.values(best).sort((a, b) => a.km - b.km);
  }, [selected, jobs, techColors]);

  return (
    <div>
      <PageHeader eyebrow="Pianificazione per zona" title="Mappa interventi">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setDate((d) => shiftDate(d, -1))} data-testid="map-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-40" data-testid="map-date-input" />
          <Button variant="outline" size="icon" onClick={() => setDate((d) => shiftDate(d, 1))} data-testid="map-next-day"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" onClick={() => setDate(todayISO())} data-testid="map-today-button">Oggi</Button>
        </div>
      </PageHeader>
      <p className="-mt-5 mb-3 text-sm text-slate-500" data-testid="map-date-label"><span className="capitalize">{fmtLongDate(date)}</span> · {tech.length} {tech.length === 1 ? "intervento tecnico" : "interventi tecnici"} · {sites.length} {sites.length === 1 ? "cantiere" : "cantieri"}</p>
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="map-layers">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Mostra</span>
        {LAYERS.map(([k, label]) => (
          <button key={k} type="button" data-active={layers[k]} onClick={() => toggle(k)} className="choice rounded-full border bg-white px-3 py-1 text-xs" data-testid={`map-layer-${k}`}>{label}</button>
        ))}
        <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} className="rounded-full border bg-white px-3 py-1 text-xs sm:ml-auto" data-testid="map-tech-filter">
          <option value="">Tutti i tecnici</option>
          {staff.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      {techFilter && <p className="-mt-2 mb-3 text-xs text-slate-500" data-testid="map-tech-filter-note">Solo {staff.find((t) => t.id === techFilter)?.name}: interventi e cantieri (anche da aiutante){layers.staff ? ", posizione e percorso del giorno (serve la seconda password)" : ""}.</p>}
      <div className="grid gap-5 xl:grid-cols-12 [&>*]:min-w-0">
        <div className="relative z-0 h-[300px] overflow-hidden rounded-2xl border xl:col-span-7 xl:h-[420px]" data-testid="jobs-map">
          <MapContainer center={DEFAULT_CENTER} zoom={6} className="h-full w-full" scrollWheelZoom>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {layers.staff && <TechTrackingLayer live={liveShown} route={tracking.route} />}
            {selected?.location && <Circle center={latlng(selected)} radius={5000} pathOptions={{ color: PENDING, fillOpacity: 0.08, weight: 1.5 }} />}
            {located.map((j) => (
              <Marker key={j.id} position={latlng(j)} icon={pin(colorOf(j), j.start.slice(0, 2), j.id === selectedId, j.kind === "cantiere")}
                eventHandlers={{ click: () => setSelectedId(j.id) }}>
                <Popup>
                  <div className="space-y-1" data-testid={`map-popup-${j.id}`}>
                    <p className="font-semibold">{j.kind === "cantiere" ? `Cantiere: ${j.site?.name || j.client.last_name}` : `${j.client.first_name} ${j.client.last_name}`}</p>
                    <p className="text-xs">{KIND[j.kind]}{j.equipment_label ? ` · ${j.equipment_label}` : ""}</p>
                    <p className="text-xs">{j.start}–{j.end} · {j.technician_name || "Da assegnare"}</p>
                    <button className="text-xs font-semibold text-[#61a229] underline" onClick={() => actions.openDetail(j)}>Dettagli</button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          {!located.length && (
            <div className="pointer-events-none absolute inset-0 z-[500] grid place-items-center">
              <p className="rounded-full bg-white/90 px-4 py-2 text-sm text-slate-600 shadow" data-testid="map-empty">Nessun elemento da mostrare con la selezione attuale</p>
            </div>
          )}
        </div>
        <aside className="space-y-4 xl:col-span-5">
          {layers.tech && <JobList title="Interventi tecnici" items={tech} testid="map-tech-list" selectedId={selectedId} colorOf={colorOf} onPick={pick}
            empty={isToday ? "Oggi non ci sono interventi tecnici in programma" : "In questa data non ci sono interventi tecnici in programma"} />}
          {layers.sites && <JobList title="Cantieri" items={sites} testid="map-site-list" selectedId={selectedId} colorOf={colorOf} onPick={pick}
            empty={isToday ? "Oggi non ci sono cantieri in programma" : "Nessun cantiere in questa data"} />}
          {selected?.status === "in_attesa" && <NearbyPanel job={selected} nearby={nearby} onAssign={actions.openAssign} />}
          {layers.staff && <TechTrackingPanel {...tracking} live={liveShown} />}
          {layers.legend && <div className="rounded-2xl border bg-white p-4">
            <SectionLabel>Legenda</SectionLabel>
            <div className="mt-3 space-y-1.5 text-sm" data-testid="map-legend">
              <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: PENDING }} />Da assegnare ({pending.length})</p>
              {legend.map((t) => <p key={t.id} className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: t.color }} />{t.name} ({t.count})</p>)}
              <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: DONE }} />Completati</p>
              <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border border-slate-400" />Intervento tecnico · <span className="h-3 w-3 rotate-45 rounded-sm border border-slate-400" />Cantiere</p>
            </div>
          </div>}
        </aside>
      </div>
      {actions.ui}
    </div>
  );
}
